import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentArrowDownIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { fetchDocument, fetchDocumentStatus } from "../../../../api/document";

const isPdfFile = (attachedDocument) => {
  const contentType = attachedDocument?.document_content_type || attachedDocument?.file?.content_type || "";
  const filename = attachedDocument?.document_file_name || attachedDocument?.file?.filename || "";
  return contentType === "application/pdf" || filename.toLowerCase().endsWith(".pdf");
};

const getFileUrl = (attachedDocument) =>
  attachedDocument?.file?.url ||
  attachedDocument?.file?.full_url ||
  null;

const fileActionClassName =
  "inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2";

/**
 * Renders an HSE document attachment (PDF inline, other formats as download).
 */
export const DocumentFileContent = ({ attachedDocument, attachedDocumentId }) => {
  const { t } = useTranslation();
  const [document, setDocument] = useState(attachedDocument || null);
  const [loading, setLoading] = useState(!attachedDocument && !!attachedDocumentId);

  const loadDocument = async (docId) => {
    const response = await fetchDocument(docId);
    return response?.data || null;
  };

  useEffect(() => {
    if (attachedDocument) {
      setDocument(attachedDocument);
      setLoading(false);
      return;
    }

    if (!attachedDocumentId) {
      setDocument(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const doc = await loadDocument(attachedDocumentId);
        if (!cancelled) {
          setDocument(doc);
        }
      } catch (err) {
        console.error("Error loading attached document:", err);
        if (!cancelled) {
          setDocument(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [attachedDocument, attachedDocumentId]);

  useEffect(() => {
    const docId = document?.id || attachedDocumentId;
    if (!docId || getFileUrl(document)) {
      return undefined;
    }

    if (document?.status === "failed") {
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      try {
        const statusResponse = await fetchDocumentStatus(docId);
        const status = statusResponse?.data?.status;

        if (cancelled) return;

        if (status === "ready") {
          const refreshed = await loadDocument(docId);
          if (!cancelled && refreshed) {
            setDocument(refreshed);
          }
          return;
        }

        if (status === "failed") {
          setDocument((prev) => ({ ...(prev || {}), id: docId, status: "failed" }));
          return;
        }

        timeoutId = window.setTimeout(poll, 1500);
      } catch (err) {
        console.error("Error polling attached document status:", err);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [document?.id, document?.status, attachedDocumentId]);

  if (loading) {
    return (
      <div className="not-prose">
        <ResourceLoading size="sm" />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="not-prose">
        <p className="text-gray-500">{t("hse.document.file_missing")}</p>
      </div>
    );
  }

  const fileUrl = getFileUrl(document);
  const filename = document.document_file_name || document.file?.filename || document.title || t("hse.document.attached_file");
  const title = document.title || filename;

  if (document.status === "failed") {
    return (
      <div className="not-prose">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-900">{t("hse.document.file_failed")}</p>
          <p className="text-xs text-red-800 mt-1">{title}</p>
        </div>
      </div>
    );
  }

  if (!fileUrl) {
    return (
      <div className="not-prose">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-900">{t("hse.document.file_processing")}</p>
          <p className="text-xs text-yellow-800 mt-1">{title}</p>
        </div>
      </div>
    );
  }

  if (isPdfFile(document)) {
    return (
      <div className="not-prose space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={fileActionClassName}
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            {t("hse.document.open_file")}
          </a>
        </div>
        <iframe
          src={fileUrl}
          title={title}
          className="w-full min-h-[70vh] rounded-lg border border-gray-200 bg-gray-50"
        />
      </div>
    );
  }

  return (
    <div className="not-prose">
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
            <DocumentTextIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
            <p className="mt-0.5 text-sm text-gray-500 truncate">{filename}</p>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={filename}
              className={`${fileActionClassName} mt-4`}
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              {t("hse.document.download_file")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
